import { $, $$, sliderConfig } from '@utils';
import Swiper from 'swiper';
import { Navigation, Scrollbar, A11y } from 'swiper/modules';
import MicroModal from 'micromodal';
import datalayerPushGeneric from '@utils/DataLayerPushGeneric';
import audioPlayer from '@blocks/audio_player/AudioPlayer';
import dataLayerPushSlider from '@utils/dataLayerPushSlider';

function init() {
  if (!$('[data-au-gallery]')) return;

  const slidersArray = [];

  setSliderModalContent();
  buildMainSlider();
  disableListPlayButtonsFocus();
  clickDetail(slidersArray);
}

/**
 * get html inside gallery before slider init, so we can clone it without any inline styles, arias...
 */
function setSliderModalContent() {
  const galleryWrapperHtmls = [];

  $$('[data-au-gallery-slider]').forEach((el, index) => {
    const gallery = el.closest('[data-au-gallery]'),
      galleryId = gallery.dataset.auGallery,
      galleryContent = $(`#${galleryId}-content`),
      gallerySection = el.closest('[data-au-gallery-section]'),
      gallerySectionTitle = gallerySection.querySelector('[data-au-heading-title]');

    if (gallerySectionTitle) {
      const gallerySectionTitleClone = gallerySectionTitle.cloneNode(true);
      gallerySectionTitleClone.classList.add('au-hide');
      galleryContent.prepend(gallerySectionTitleClone);
    }

    galleryWrapperHtmls.push(el.innerHTML);
    galleryContent.innerHTML = galleryWrapperHtmls[index];
    galleryContent.classList.add('swiper', 'au-gallery__modal-content');
    galleryContent.setAttribute('data-au-gallery-modal-slider', '');

    galleryContent.querySelectorAll('.swiper-slide').forEach((slide) => {
      const picture = slide.querySelector('picture[data-au-gallery-picture]');
      const audioPlayer = slide.querySelector('[data-au-audio-player]');

      if (!picture || !audioPlayer) return;
      if (picture.closest('.au-gallery-slide__container') || audioPlayer.closest('.au-gallery-slide__container'))
        return;

      const container = document.createElement('div');
      container.className = 'au-gallery-slide__container';

      const parent = picture.parentNode;
      parent.insertBefore(container, picture);
      container.appendChild(picture);
      container.appendChild(audioPlayer);
    });

    galleryContent.querySelectorAll('[data-au-card-video]').forEach((video) => video.remove());
    galleryContent.querySelectorAll('[data-au-gallery-actions]').forEach((actions) => actions.remove());

    galleryContent.querySelectorAll('[data-au-gallery-picture]').forEach((el) => {
      const pictureSource = el.querySelector('source');
      const pictureImg = el.querySelector('img');
      pictureSource.setAttribute('srcset', pictureSource.dataset.auGalleryModalSrcset);
      pictureImg.setAttribute('src', pictureImg.dataset.auGalleryModalSrc);
    });

    galleryContent
      .querySelectorAll('[data-au-audio-player-play][data-au-gallery-btn-detail]')
      .forEach((btn) => btn.removeAttribute('data-au-gallery-btn-detail'));

    galleryContent.querySelectorAll('[data-au-audio-player-preview]').forEach((p) => {
      p.removeAttribute('data-au-audio-player-preview');
    });

    galleryContent.querySelectorAll('[data-au-audio-player-init]').forEach((p) => {
      p.removeAttribute('data-au-audio-player-init');
    });
  });
}

/**
 * disable focus audio play button
 */
function disableListPlayButtonsFocus() {
  $$('[data-au-gallery-slider] [data-au-audio-player-play]').forEach((btn) => btn.setAttribute('tabindex', '-1'));
}

/**
 * @param el
 * play video inside modal gallery
 */
function playModalGalleryVideo(el) {
  const slideActive = el.querySelectorAll('[data-au-gallery-slide]')[el.swiper.activeIndex];
  const iframe = slideActive.querySelector('[data-au-gallery-iframe]');
  if (!iframe) return;

  const src = iframe.dataset.auGalleryIframe;

  if (iframe) {
    iframe.setAttribute('src', `${src}?rel=0&autoplay=1`);
    iframe.classList.add('au-gallery-slide__video--visible');
  }
}

/**
 * play audio in active modal slide
 */
function playModalGalleryAudio(el) {
  if (!el?.swiper) return;

  const activeSlide = el.querySelectorAll('[data-au-gallery-slide]')[el.swiper.activeIndex];
  const audio = activeSlide?.querySelector('[data-au-audio-player-audio]');
  // Load src dynamically if not already loaded
  if (audio && !audio.src) {
    const audioSrc = audio.getAttribute('data-au-audio-player-audio');
    if (audioSrc) {
      audio.src = audioSrc;
    }
  }

  if (!audio) return;

  audio.play().catch(() => {});
}

/**
 * stop all modal gallery videos
 */
function stopAllModalGalleryVideos() {
  $$('[data-au-gallery-iframe]').forEach((iframeVideo) => {
    iframeVideo.setAttribute('src', '');
  });
}

/**
 * stop all modal audios
 */
function stopAllAudios() {
  $$('[data-au-audio-player-audio]').forEach((audio) => {
    audio.__userTriggered = false;
    audio.pause();
  });
}

/**
 * build main slider
 */
function buildMainSlider() {
  $$('[data-au-gallery-slider]').forEach((slider) => {
    new Swiper(slider, {
      modules: [Navigation, Scrollbar, A11y],
      slideToClickedSlide: true,
      ...sliderConfig,
      slidesPerView: 1.125,
      breakpoints: {
        768: {
          slidesPerView: 2.325,
        },
      },
    });
  });
}
/**
 * Only active slide tab modal
 */
function setTabbableOnlyActive(swiper) {
  const focusables = 'a,button,input,select,textarea,[tabindex],[contenteditable="true"]';

  swiper.slides.forEach((slideEl, i) => {
    const isActive = i === swiper.activeIndex;

    // Block focus on inactive slides in modals
    slideEl.toggleAttribute('inert', !isActive);
    slideEl.setAttribute('aria-hidden', String(!isActive));

    slideEl.querySelectorAll(focusables).forEach((el) => {
      if (isActive) {
        if (el.__prevTabIndex !== undefined) {
          if (el.__prevTabIndex === null) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', el.__prevTabIndex);
          delete el.__prevTabIndex;
        } else if (el.getAttribute('tabindex') === '-1') {
          el.removeAttribute('tabindex');
        }
      } else {
        if (el.__prevTabIndex === undefined) {
          el.__prevTabIndex = el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : null;
        }
        el.setAttribute('tabindex', '-1');
      }
    });
  });
}

/**
 * @param slidersArray sliders array. we push to this array every slider that we create. when close modal, destroy all
 * @param galleryIndex index of gallery. just build the modal slider inside clicked gallery
 * @param initialSlide initial slide, corresponding to clicked index card
 * build modal slider
 */
function buildModalSlider(slidersArray, initialSlide) {
  const slider = $('[data-au-gallery-modal-slider]');
  const newsSlider = new Swiper(slider, {
    modules: [Navigation, Scrollbar, A11y],
    initialSlide,
    ...sliderConfig,
    on: {
      init() {
        setTabbableOnlyActive(this);
        playModalGalleryVideo(slider);
        playModalGalleryAudio(slider);
      },
      slideChange() {
        setTabbableOnlyActive(this);
        stopAllModalGalleryVideos();
        audioPlayer.resetWithin();
        playModalGalleryVideo(slider);
        playModalGalleryAudio(slider);
      },
      touchEnd(swiper, event) {
        dataLayerPushSlider(swiper, event);
      },
    },
  });

  slidersArray.push(newsSlider);
}

/**
 * function triggered at click on gallery slide
 */
function clickDetail(slidersArray) {
  $$('[data-au-gallery-slide]').forEach((slide) => {
    slide.addEventListener(
      'click',
      (e) => {
        const trigger = e.target.closest('[data-au-gallery-btn-detail]');
        if (!trigger) return;

        //If the trigger is the play button in the list, we prevent it from playing the "original" audio
        if (trigger.matches('[data-au-audio-player-play]')) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }

        toggleGalleryModal(e, slidersArray, trigger);
      },
      true, // capture to be able to cut the click before the audio player handler
    );
  });
}

/**
 * open/close modal
 */
function toggleGalleryModal(e, slidersArray, trigger) {
  const button = trigger;
  const slide = button.closest('[data-au-gallery-slide]');

  const slideTitle = slide.querySelector('[data-au-gallery-slide-title]')
    ? slide.querySelector('[data-au-gallery-slide-title]').innerText
    : '';

  const slideIndex = Array.from(slide.parentNode.children).indexOf(slide);

  const gallerySection = button.closest('[data-au-gallery-section]');
  const gallerySectionTitle = gallerySection.querySelector('[data-au-heading-title]')
    ? gallerySection.querySelector('[data-au-heading-title]').innerText
    : '';

  const gallery = button.closest('[data-au-gallery]');
  const galleryId = gallery.dataset.auGallery;
  const audioTitle = slide.querySelector('[data-au-audio-player-audio]')?.getAttribute('title')?.trim() || '';

  MicroModal.show(galleryId, {
    onShow: () => {
      const modalEl = document.getElementById(galleryId);
      if (!modalEl) return;

      buildModalSlider(slidersArray, slideIndex, modalEl);
      audioPlayer.initWithin(modalEl);

      modalEl.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-au-audio-player-play]');
        if (!btn) return;

        const audio = btn.closest('[data-au-audio-player]')?.querySelector('[data-au-audio-player-audio]');

        if (audio) {
          audio.__userTriggered = true;
        }
      });
      // Datalayer
      if (button.dataset.auGalleryModalVideo) {
        datalayerPushGeneric(e, 'navigation', 'clickVideo', slideTitle);
      }
      if (button.hasAttribute('data-au-gallery-modal-audio')) {
        datalayerPushGeneric(e, 'navigation', 'clickBoton', audioTitle, 'play');
      } else {
        datalayerPushGeneric(e, 'navigation', 'clickImagen', gallerySectionTitle, slideTitle || `imagen${slideIndex}`);
      }
    },

    onClose: () => {
      const modalEl = document.getElementById(galleryId);

      stopAllModalGalleryVideos(modalEl);
      stopAllAudios(document);
      audioPlayer.resetWithin(document);

      slidersArray.forEach((s) => s.destroy());
      slidersArray.length = 0;

      button.blur();
      datalayerPushGeneric(e, 'navigation', 'clickBoton', slideTitle, 'cerrar');
    },
  });

  const modalEl = document.getElementById(galleryId);
  if (!modalEl) return;

  modalEl
    .querySelectorAll('[data-au-audio-player-preview]')
    .forEach((p) => p.removeAttribute('data-au-audio-player-preview'));

  audioPlayer.initWithin(modalEl);
  stopAllAudios(document);
  audioPlayer.resetWithin(modalEl);

  const modalSliderEl = modalEl.querySelector('[data-au-gallery-modal-slider]');
  const slides = modalSliderEl ? modalSliderEl.querySelectorAll('[data-au-gallery-slide]') : [];
  const targetSlide = slides[slideIndex];

  const audio = targetSlide?.querySelector('[data-au-audio-player-audio]');
  if (audio) {
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }
}

export default { init };
